import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { getTenantToday } from '../utils/dateUtils';
import { motion } from 'framer-motion';
import {
  Briefcase,
  BarChart3,
  Shield,
  RefreshCcw,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  Target,
  BookOpen
} from 'lucide-react';
import AIChatPanel from './AIChatPanel';
import { cn } from '../lib/utils';
import {
  fetchClassifications,
  dimensionValue,
  PIVOT_DIMENSION_LABELS,
  type PivotDimension,
  type SecurityClassification,
} from './classificationPivot';

import { DateField } from './shared/DateField';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; trend?: string; subtitle?: string; color: string }> =
  ({ label, value, trend, subtitle, color }) => (
    <motion.div
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
      <div className="text-2xl font-bold text-white tracking-tight">{value}</div>
      {trend && (
        <div className={cn("text-[10px] font-bold mt-2", trend.startsWith('+') ? "text-emerald-400" : "text-red-400")}>
          {trend} vs last period
        </div>
      )}
      {subtitle && !trend && (
        <div className="text-[10px] font-medium mt-2 text-slate-500 truncate">{subtitle}</div>
      )}
    </motion.div>
  );

// ============== Constants ==============
const SECTOR_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

// ============== Interfaces ==============
interface Position {
  id: number;
  portfolioId: number;
  portfolioName: string;
  portfolioCode?: string;
  securityId: number;
  securityName: string;
  cusip: string;
  issuerId?: number;
  issuerName: string;
  assetClassName?: string;
  assetSubClassName?: string;
  quantity: number;
  averagePrice: number;
  marketValue: number;
  costBasis: number;
  unrealizedPnl: number;
  parAmount?: number;
}

interface SectorData {
  name: string;
  marketValue: number;
  percentage: number;
  count: number;
}

interface IssuerData {
  id?: number;
  name: string;
  marketValue: number;
  percentage: number;
  count: number;
}

interface TopHolding {
  id: number;
  securityName: string;
  cusip: string;
  issuerName: string;
  portfolioName: string;
  marketValue: number;
  percentage: number;
  unrealizedPnl: number;
}

interface PortfolioData {
  portfolioId: number;
  portfolioName: string;
  marketValue: number;
  positionCount: number;
  percentage: number;
}

const normalizeNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const HoldingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [positions, setPositions] = useState<Position[]>([]);
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(getTenantToday());
  const [isLoading, setIsLoading] = useState(false);
  const [classifications, setClassifications] = useState<Map<number, SecurityClassification>>(new Map());
  const [allocationDim, setAllocationDim] = useState<PivotDimension>('L1');

  // Same IBOR replay selection as the position register: trade-date positions
  // as of the selected date, official IBOR rows only. The legacy /api/positions
  // exact-date query silently dropped every row not re-stamped on that date.
  const fetchPositions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        validAsOf: selectedDate || 'now',
        knowledgeAsOf: 'now',
        basis: 'TRADE_DATE',
      };
      if (selectedPortfolioId) params.portfolioId = selectedPortfolioId;
      const response = await axios.get('/api/ibor/replay', { params });
      const raw = response.data?.positions || [];
      const data = (Array.isArray(raw) ? raw : []).map((p: any) => ({
        ...p,
        assetClassName: p.assetClassName || p.assetSubClassName || null,
        quantity: normalizeNumber(p.quantity),
        averagePrice: p.averagePrice == null ? null : normalizeNumber(p.averagePrice),
        marketValue: normalizeNumber(p.marketValue),
        costBasis: normalizeNumber(p.costBasis),
        unrealizedPnl: normalizeNumber(p.unrealizedPnl),
        parAmount: normalizeNumber(p.parAmount),
      }));
      setPositions(data);
    } catch (error) {
      console.error('Error fetching positions:', error);
      setPositions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPortfolioId, selectedDate]);

  useEffect(() => {
    fetchPositions();
    axios.get('/api/dimensions/portfolios').then(res => setPortfolios(res.data?.content || res.data || []));
  }, [fetchPositions]);

  // Canonical classification (Level 1) drives the allocation panel and the
  // cash carve-out, resolved at the same as-of date as the positions.
  useEffect(() => {
    if (positions.length === 0) {
      setClassifications(new Map());
      return;
    }
    const ids = Array.from(new Set(positions.map(p => p.securityId).filter(Boolean)));
    let cancelled = false;
    fetchClassifications(ids, selectedDate, '')
      .then(map => { if (!cancelled) setClassifications(map); })
      .catch(error => {
        console.error('Error resolving classifications:', error);
        if (!cancelled) setClassifications(new Map());
      });
    return () => { cancelled = true; };
  }, [positions, selectedDate]);

  const isCashRow = useCallback((p: Position): boolean => {
    const cls = classifications.get(p.securityId);
    if (cls?.l1Name) return cls.l1Name === 'Cash & Equivalents';
    return String(p.assetClassName || '').toUpperCase().includes('CASH')
      || String(p.cusip || '').toUpperCase().endsWith('_CASH');
  }, [classifications]);

  // ============== Computed Analytics ==============

  const stats = useMemo(() => {
    const totalMV = positions.reduce((sum, p) => sum + (p.marketValue || 0), 0);
    const totalPnl = positions.reduce((sum, p) => sum + (p.unrealizedPnl || 0), 0);
    return { totalMV, totalPnl, count: positions.length };
  }, [positions]);

  // One classification dimension at a time (Level 1 / Level 2 / Credit
  // Quality) — same bucketing as the register pivot. Deep drill-down lives in
  // the register, not here.
  const classAllocation = useMemo<SectorData[]>(() => {
    const totalMV = positions.reduce((s, p) => s + (p.marketValue || 0), 0);
    const groups = new Map<string, { mv: number; count: number }>();
    positions.forEach(p => {
      const key = dimensionValue(classifications.get(p.securityId), allocationDim);
      const existing = groups.get(key) || { mv: 0, count: 0 };
      groups.set(key, { mv: existing.mv + (p.marketValue || 0), count: existing.count + 1 });
    });
    return Array.from(groups.entries())
      .map(([name, data]) => ({
        name,
        marketValue: data.mv,
        percentage: totalMV > 0 ? (data.mv / totalMV) * 100 : 0,
        count: data.count
      }))
      .sort((a, b) => b.marketValue - a.marketValue);
  }, [positions, classifications, allocationDim]);

  // Issuer concentration measures invested exposure: cash is excluded and
  // percentages are of the full ex-cash book. Positions without an issuer
  // mapping are excluded from the ranking (they are not one issuer) but stay
  // in the denominator, so named percentages are honest floors; the gap is
  // surfaced separately instead of masquerading as a giant "Unknown" issuer.
  const issuerExposure = useMemo<{ issuers: IssuerData[]; investedMarketValue: number; mappedMarketValue: number; coveragePct: number; unmapped: { count: number; marketValue: number; percentage: number } }>(() => {
    const invested = positions.filter(p => !isCashRow(p));
    const totalMV = invested.reduce((s, p) => s + (p.marketValue || 0), 0);
    // Group by name (display key) but capture the first non-null issuerId we see —
    // any position from this issuer will share the same id, so first-wins is fine.
    const groups = new Map<string, { id?: number; mv: number; count: number }>();
    let unmappedMV = 0;
    let unmappedCount = 0;
    invested.forEach(p => {
      if (!p.issuerName) {
        unmappedMV += p.marketValue || 0;
        unmappedCount += 1;
        return;
      }
      const existing = groups.get(p.issuerName) || { id: undefined, mv: 0, count: 0 };
      groups.set(p.issuerName, {
        id: existing.id ?? p.issuerId,
        mv: existing.mv + (p.marketValue || 0),
        count: existing.count + 1
      });
    });
    const issuers = Array.from(groups.entries())
      .map(([name, data]) => ({
        id: data.id,
        name,
        marketValue: data.mv,
        percentage: totalMV > 0 ? (data.mv / totalMV) * 100 : 0,
        count: data.count
      }))
      .sort((a, b) => b.marketValue - a.marketValue);
    const mappedMV = totalMV - unmappedMV;
    return {
      issuers,
      investedMarketValue: totalMV,
      mappedMarketValue: mappedMV,
      coveragePct: totalMV > 0 ? (mappedMV / totalMV) * 100 : 0,
      unmapped: {
        count: unmappedCount,
        marketValue: unmappedMV,
        percentage: totalMV > 0 ? (unmappedMV / totalMV) * 100 : 0
      }
    };
  }, [positions, isCashRow]);

  // When most of the invested book is real-asset / fund / derivative exposure
  // with no single-name issuer, a small "top issuer %" is NOT a diversification
  // signal — it just means concentration can only be measured over the mapped
  // sleeve. Flag that so the headline is never read in isolation (F-HOLD-001).
  const lowIssuerCoverage = issuerExposure.coveragePct < 80;

  const issuerConcentration = issuerExposure.issuers;

  const topHoldings = useMemo<TopHolding[]>(() => {
    const invested = positions.filter(p => !isCashRow(p));
    const totalMV = invested.reduce((s, p) => s + (p.marketValue || 0), 0);
    return invested
      .sort((a, b) => (b.marketValue || 0) - (a.marketValue || 0))
      .slice(0, 10)
      .map(p => ({
        id: p.id,
        securityName: p.securityName,
        cusip: p.cusip,
        issuerName: p.issuerName || 'Unknown',
        portfolioName: p.portfolioName,
        marketValue: p.marketValue || 0,
        percentage: totalMV > 0 ? ((p.marketValue || 0) / totalMV) * 100 : 0,
        unrealizedPnl: p.unrealizedPnl || 0
      }));
  }, [positions, isCashRow]);

  const portfolioComparison = useMemo<PortfolioData[]>(() => {
    if (selectedPortfolioId) return [];
    const totalMV = positions.reduce((s, p) => s + (p.marketValue || 0), 0);
    const groups = new Map<number, { name: string; mv: number; count: number }>();
    positions.forEach(p => {
      const existing = groups.get(p.portfolioId) || { name: p.portfolioName, mv: 0, count: 0 };
      groups.set(p.portfolioId, { name: p.portfolioName, mv: existing.mv + (p.marketValue || 0), count: existing.count + 1 });
    });
    return Array.from(groups.entries())
      .map(([id, data]) => ({
        portfolioId: id,
        portfolioName: data.name,
        marketValue: data.mv,
        positionCount: data.count,
        percentage: totalMV > 0 ? (data.mv / totalMV) * 100 : 0
      }))
      .sort((a, b) => b.marketValue - a.marketValue);
  }, [positions, selectedPortfolioId]);

  const topConcentration = useMemo(() => {
    if (issuerConcentration.length === 0) return { percentage: 0, name: '' };
    return { percentage: issuerConcentration[0].percentage, name: issuerConcentration[0].name };
  }, [issuerConcentration]);

  // Donut gradient for issuer concentration
  const donutGradient = useMemo(() => {
    const top5 = issuerConcentration.slice(0, 5);
    if (top5.length === 0) return 'conic-gradient(#334155 0deg 360deg)';
    let cumulative = 0;
    const segments: string[] = [];
    top5.forEach((issuer, i) => {
      const start = cumulative * 3.6;
      cumulative += issuer.percentage;
      const end = cumulative * 3.6;
      segments.push(`${SECTOR_COLORS[i % SECTOR_COLORS.length]} ${start}deg ${end}deg`);
    });
    if (cumulative < 100) {
      segments.push(`#334155 ${cumulative * 3.6}deg 360deg`);
    }
    return `conic-gradient(${segments.join(', ')})`;
  }, [issuerConcentration]);

  // ============== Helper: Format currency ==============
  const formatMV = (val: number): string => {
    const abs = Math.abs(val);
    const prefix = val < 0 ? '-$' : '$';
    if (abs >= 1_000_000_000) return `${prefix}${(abs / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000) return `${prefix}${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${prefix}${(abs / 1_000).toFixed(0)}K`;
    return `${prefix}${abs.toFixed(0)}`;
  };

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-blue-500" />
                Holdings & Exposure
              </h1>
              <p className="text-slate-500 font-medium mt-1">Portfolio exposure analysis, concentration risk, and position details</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/ibor')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
                title="Row-level positions, tax lots and bi-temporal replay"
              >
                <BookOpen className="w-4 h-4" />
                IBOR Register
              </button>
              <button
                onClick={() => fetchPositions()}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Portfolio Value" value={formatMV(stats.totalMV)} color="#3b82f6" subtitle="IBOR trade-date basis" />
            <StatCard label="Unrealized P&L" value={formatMV(stats.totalPnl)} color={stats.totalPnl >= 0 ? "#10b981" : "#ef4444"} subtitle="Mark-to-market" />
            <StatCard label="Total Positions" value={String(stats.count)} color="#8b5cf6" />
            <StatCard
              label="Top Issuer"
              value={`${topConcentration.percentage.toFixed(1)}%`}
              color="#f59e0b"
              subtitle={topConcentration.name
                ? `${topConcentration.name} · ${issuerExposure.coveragePct.toFixed(0)}% of book issuer-mapped`
                : undefined}
            />
          </div>
        </motion.div>

        {/* Filter bar */}
        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Portfolio</label>
                <select
                  value={selectedPortfolioId}
                  onChange={(e) => setSelectedPortfolioId(e.target.value)}
                  className="bg-slate-950/50 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500/50 min-w-[180px]"
                >
                  <option value="">All Portfolios</option>
                  {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Date</label>
                <DateField compact
                  value={selectedDate ?? ''}
                  onChange={iso => setSelectedDate(iso)}
                />
              </div>
            </div>
            <div className="text-[10px] font-medium text-slate-600">
              {positions.length} positions as of {selectedDate}
            </div>
          </div>
        </GlassCard>

        {/* Exposure Analytics */}
        <div className="space-y-6">
          {positions.length === 0 && !isLoading ? (
            <GlassCard className="p-12 text-center">
              <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <div className="text-sm text-slate-500">No positions found for the selected date and portfolio</div>
            </GlassCard>
          ) : (
            <>
              {/* Row 1: Class Allocation + Issuer Concentration */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Allocation by canonical classification, one dimension at a time */}
                <GlassCard className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest">Allocation</h3>
                    <div className="ml-auto flex items-center gap-2">
                      <div className="flex bg-slate-950/50 rounded-lg p-0.5 border border-white/10">
                        {(Object.keys(PIVOT_DIMENSION_LABELS) as PivotDimension[]).map(dim => (
                          <button
                            key={dim}
                            onClick={() => setAllocationDim(dim)}
                            className={cn(
                              "px-2.5 py-1 rounded-md text-[10px] font-bold transition-all",
                              allocationDim === dim ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-300"
                            )}
                          >
                            {PIVOT_DIMENSION_LABELS[dim]}
                          </button>
                        ))}
                      </div>
                      <span className="text-[10px] text-slate-600">{classAllocation.length} buckets</span>
                    </div>
                  </div>
                  <div className="space-y-5">
                    {classAllocation.map((sector, i) => (
                      <div key={sector.name}>
                        <div className="flex justify-between items-end text-xs mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SECTOR_COLORS[i % SECTOR_COLORS.length] }} />
                            <span className="font-semibold text-slate-300">{sector.name}</span>
                            <span className="text-slate-600">({sector.count})</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-slate-500 font-mono text-[11px]">{formatMV(sector.marketValue)}</span>
                            <span className="text-white font-bold w-14 text-right">{sector.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${sector.percentage}%` }}
                            transition={{ duration: 0.8, delay: i * 0.08, ease: "easeOut" as const }}
                            className="h-full rounded-full"
                            style={{ backgroundColor: SECTOR_COLORS[i % SECTOR_COLORS.length] }}
                          />
                        </div>
                      </div>
                    ))}
                    {classAllocation.length === 0 && (
                      <div className="text-xs text-slate-600 text-center py-8">No asset class data available</div>
                    )}
                  </div>
                </GlassCard>

                {/* Issuer Concentration */}
                <GlassCard className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="w-5 h-5 text-amber-400" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest">Issuer Concentration</h3>
                    <span className="text-[10px] text-slate-600 ml-auto">{issuerConcentration.length} issuers · {issuerExposure.coveragePct.toFixed(0)}% of invested mapped</span>
                  </div>
                  {lowIssuerCoverage && issuerExposure.unmapped.count > 0 && (
                    <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-[11px] leading-relaxed text-amber-200/90">
                        Issuer concentration below covers only <span className="font-bold">{issuerExposure.coveragePct.toFixed(0)}%</span> of the {formatMV(issuerExposure.investedMarketValue)} invested book.
                        The remaining <span className="font-bold">{formatMV(issuerExposure.unmapped.marketValue)}</span> ({issuerExposure.unmapped.percentage.toFixed(0)}%) is real-asset, fund and derivative exposure with no single-name issuer —
                        a low top-issuer figure here is <span className="font-bold">not</span> a whole-book diversification signal.
                      </div>
                    </div>
                  )}
                  <div className="flex gap-8">
                    {/* Donut */}
                    <div className="flex-shrink-0 relative">
                      <div
                        className="w-40 h-40 rounded-full"
                        style={{ background: donutGradient }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-24 h-24 rounded-full bg-slate-900 flex flex-col items-center justify-center">
                          <div className="text-xl font-bold text-white">{topConcentration.percentage.toFixed(topConcentration.percentage >= 10 ? 0 : 1)}%</div>
                          <div className="text-[9px] text-slate-500 uppercase tracking-widest">Top Issuer</div>
                          {lowIssuerCoverage && (
                            <div className="text-[8px] text-amber-400/80 mt-0.5">of {issuerExposure.coveragePct.toFixed(0)}% mapped</div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex-1 space-y-3 pt-2">
                      {issuerConcentration.slice(0, 5).map((issuer, i) => (
                        <div
                          key={issuer.name}
                          className={`flex items-center justify-between ${issuer.id ? 'cursor-pointer hover:bg-slate-800/40 rounded px-1 -mx-1' : ''}`}
                          onClick={() => { if (issuer.id) navigate(`/issuers/${issuer.id}`); }}
                          title={issuer.id ? 'Open issuer profile' : ''}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: SECTOR_COLORS[i % SECTOR_COLORS.length] }} />
                            <span className={`text-xs truncate ${issuer.id ? 'text-blue-300 hover:text-blue-200' : 'text-slate-400'}`}>{issuer.name}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            <span className="text-[11px] font-mono text-slate-500">{formatMV(issuer.marketValue)}</span>
                            <span className="text-xs font-bold text-white w-12 text-right">{issuer.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      ))}
                      {issuerConcentration.length > 5 && (
                        <div className="text-[10px] text-slate-600 pt-1">
                          +{issuerConcentration.length - 5} more issuers
                        </div>
                      )}
                      {issuerExposure.unmapped.count > 0 && (
                        <div className="text-[10px] text-amber-500/80 pt-1">
                          {issuerExposure.unmapped.count} positions · {formatMV(issuerExposure.unmapped.marketValue)} ({issuerExposure.unmapped.percentage.toFixed(1)}% of invested) have no issuer mapping
                        </div>
                      )}
                      {issuerConcentration.length === 0 && issuerExposure.unmapped.count === 0 && (
                        <div className="text-xs text-slate-600 text-center py-8">No issuer data available</div>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </div>

              {/* Row 2: Top Holdings */}
              <GlassCard className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-widest">Top 10 Holdings</h3>
                  <span className="text-[10px] text-slate-600 ml-auto">by market value · ex-cash</span>
                </div>
                <div className="divide-y divide-white/5">
                  {topHoldings.map((h, i) => (
                    <div key={h.id} className="flex items-center gap-4 py-3 group hover:bg-white/[0.02] px-2 -mx-2 rounded-lg transition-colors">
                      <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center text-[11px] font-bold text-slate-500 flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{h.securityName}</div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-2">
                          <span>{h.issuerName}</span>
                          <span className="text-slate-700">|</span>
                          <span className="font-mono">{h.cusip}</span>
                          {!selectedPortfolioId && (
                            <>
                              <span className="text-slate-700">|</span>
                              <span className="text-blue-400/60">{h.portfolioName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <div className="w-32">
                          <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${h.percentage}%` }}
                              transition={{ duration: 0.6, delay: i * 0.05 }}
                              className="h-full bg-blue-500/50 rounded-full"
                            />
                          </div>
                        </div>
                        <div className="text-right w-20">
                          <div className="text-sm font-bold text-white">{formatMV(h.marketValue)}</div>
                          <div className="text-[10px] text-slate-500">{h.percentage.toFixed(1)}%</div>
                        </div>
                        <div className={cn("text-right w-24 text-xs font-bold", h.unrealizedPnl >= 0 ? "text-emerald-400" : "text-red-400")}>
                          {h.unrealizedPnl >= 0 ? '↑' : '↓'} {formatMV(h.unrealizedPnl)}
                        </div>
                      </div>
                    </div>
                  ))}
                  {topHoldings.length === 0 && (
                    <div className="text-xs text-slate-600 text-center py-8">No holdings to display</div>
                  )}
                </div>
              </GlassCard>

              {/* Row 3: Portfolio Comparison (only when All Portfolios selected) */}
              {!selectedPortfolioId && portfolioComparison.length > 1 && (
                <GlassCard className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Target className="w-5 h-5 text-purple-400" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest">Portfolio Comparison</h3>
                    <span className="text-[10px] text-slate-600 ml-auto">{portfolioComparison.length} portfolios</span>
                  </div>
                  <div className="space-y-4">
                    {portfolioComparison.map((pf, i) => (
                      <div
                        key={pf.portfolioId}
                        className="group cursor-pointer hover:bg-white/[0.02] p-3 -mx-3 rounded-lg transition-colors"
                        onClick={() => setSelectedPortfolioId(String(pf.portfolioId))}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SECTOR_COLORS[i % SECTOR_COLORS.length] }} />
                            <span className="text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">{pf.portfolioName}</span>
                            <span className="text-[10px] text-slate-600">{pf.positionCount} positions</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-bold text-white">{formatMV(pf.marketValue)}</span>
                            <span className="text-xs text-slate-500 w-14 text-right">{pf.percentage.toFixed(1)}%</span>
                            <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-blue-400 transition-colors" />
                          </div>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pf.percentage}%` }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            className="h-full rounded-full"
                            style={{ backgroundColor: SECTOR_COLORS[i % SECTOR_COLORS.length] }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              )}
            </>
          )}
        </div>
      </div>

      <AIChatPanel context={selectedPortfolioId ? { portfolioId: parseInt(selectedPortfolioId) } : undefined} />
    </Container>
  );
};

export default HoldingsScreen;
